"use strict";

import jQuery from 'jquery';
import Vue from 'vue';

/**
 * Benachrichtigungstypen
 * @enum {string}
 */
gc.gis.ui.GCNotificationType = {
	INFO:    'info',
	WARNING: 'warning',
	ERROR:   'error',
	DEBUG:   'debug'
};

/**
 * @typedef gc.gis.ui.GCInteractionMessage
 * @type {Object}
 * @property {string} title Überschrift
 * @property {string} icon Icon-CSS-Klasse
 * @property {string} message Bedienungstext
 */

/**
 * Eventtypen
 * @enum {string}
 */
gc.gis.ui.GCUserInterfaceEvent = {
	/**
	 * @event gc.gis.ui.GCUserInterfaceEvent#change:fullscreen
	 * @property {boolean} active
	 */
	FULLSCREEN_CHANGED: 'change:fullscreen',
	/**
	 * @event gc.gis.ui.GCUserInterfaceEvent#toggle:footer
	 * @property {boolean} active
	 */
	TOGGLE_FOOTER: 'toggle:footer',
	/**
	 * @event gc.gis.ui.GCUserInterfaceEvent#toggle:controls
	 * @property {boolean} active
	 */
	TOGGLE_MAP_CONTROLS: 'toggle:controls',
	/**
	 * @event gc.gis.ui.GCUserInterfaceEvent#toggle:controls
	 * @property {Number} numOld
	 * @property {Number} numNew
	 */
	EXCLUDE_MESSAGES_CHANGED: 'change:exclude_messages'
};

/**
 * @classdesc
 * Klasse für den Zugriff auf Userinterface-Elemente.
 *
 * <h4>Aufbau des Layouts:</h4>
 * <pre>
 * +---------------------------------------------------------------------------------------------+
 * | #gc_gis_webclient                                                      [position: absolute] |
 * | +------------------------+ +--------------------------------------------------------------+ |
 * | | .gc-gis-sidebar        | | .gc-gis-frame                               [display: block] | |
 * | |                        | |                                         [position: absolute] | |
 * | |       [display: block] | | +----------------------------------------------------------+ | |
 * | |   [position: absolute] | | | .gc-gis-frame-content                   [display: table] | | |
 * | |                        | | | +------------------------------------------------------+ | | |
 * | | Siehe:                 | | | | .gc-gis-header                  [display: table-row] | | | |
 * | |    gc.gis.ui.GCSidebar | | | +------------------------------------------------------+ | | |
 * | |                        | | |                                                          | | |
 * | |                        | | | +------------------------------------------------------+ | | |
 * | |                        | | | | .gc-gis-main-wrapper            [display: table-row] | | | |
 * | |                        | | | | +--------------------------------------------------+ | | | |
 * | |                        | | | | |                            [display: table-cell] | | | | |
 * | |                        | | | | | .gc-gis-main                                     | | | | |
 * | |                        | | | | |                                                  | | | | |
 * | |                        | | | | +--------------------------------------------------+ | | | |
 * | |                        | | | +------------------------------------------------------+ | | |
 * | |                        | | |                                                          | | |
 * | |                        | | | +------------------------------------------------------+ | | |
 * | |                        | | | | .gc-gis-footer                  [display: table-row] | | | |
 * | |                        | | | +------------------------------------------------------+ | | |
 * | |                        | | +----------------------------------------------------------+ | |
 * | +------------------------+ +--------------------------------------------------------------+ |
 * +---------------------------------------------------------------------------------------------+
 * </pre>
 *
 * @class
 * @extends gc.gis.GCObservable
 * @memberof gc.gis.ui
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @param {Object} options Aufrufoptionen
 */
gc.gis.ui.GCUserInterface = function( options )
{
	gc.gis.GCObservable.call( this );

	/**
	 * Schlüssel zum Ablegen der exkludierten Nachrichten im LocalStorage.
	 * @type {string}
	 * @private
	 */
	this.STORAGE_KEY = 'gis_webclient_message_excludes';

	/**
	 * @public
	 * @type {string}
	 */
	this.id = options.id;

	/**
	 * @protected
	 * @type {boolean}
     */
	this._notification_active = false;
	/**
	 * @protected
	 * @type {number}
     */
	this._notification_timeout = options.notificationTimeout || 3000;

	/**
	 * @protected
	 * @type {Object<string,boolean>}
     */
	this._interactive_message_excludes = {};
	/**
	 * @protected
	 * @type {Object[]}
	 */
	this._interaction_message_queue = [];
	/**
	 * @protected
	 * @type {Object<string,*>}
     */
	this._last_interaction_message = undefined;
	/**
	 * @protected
	 * @type {JQuery}
	 */
	this._interaction_message = undefined;
	/**
	 * @protected
	 * @type {JQuery}
     */
	this._interaction_message_content = undefined;
	/**
	 * @protected
	 * @type {JQuery}
	 */
	this._interaction_message_exclude_btn = undefined;
	/**
	 * @protected
	 * @type {JQuery}
	 */
	this._interaction_message_title = undefined;

	/**
	 * @public
	 * @type {jQuery}
     */
	this.frame = undefined;
	/**
	 * @public
	 * @type {jQuery}
     */
	this.main = undefined;
	/**
	 * @public
	 * @type {gc.gis.ui.GCSidebar}
     */
	this.sidebar = undefined;
	/**
	 * @public
	 * @type {gc.gis.ui.GCNavbar}
	 */
	this.header = undefined;
	/**
	 * @public
	 * @type {gc.gis.ui.GCNavbar}
	 */
	this.footer = undefined;
	/**
	 * Toolbar für die Zoomwerkzeuge (in der Regel rechts, vertikal)
	 * @public
	 * @type {gc.gis.ui.GCToolbar}
	 */
	this.mapToolbarZoom = undefined;
	/**
	 * Toolbar für die Rotation und Historie (in der Regel oben, horizontal)
	 * @public
	 * @type {gc.gis.ui.GCToolbar}
	 */
	this.mapToolbarRotate = undefined;
	/**
	 * @protected
	 * @type {jQuery}
	 */
	this._notification_area = undefined;
};
gc.gis.ui.GCUserInterface.prototype = Object.create( gc.gis.GCObservable.prototype );

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.init = function()
{
	this._interactive_message_excludes = gc.gis.GCStorage.getItem(
		this.STORAGE_KEY, gc.gis.GCStorageType.LOCAL_STORAGE);
	this._interactive_message_excludes = this._interactive_message_excludes !== undefined
		? JSON.parse(this._interactive_message_excludes) : {};

	this._initUI();
	this._initEvents();
};

/**
 * @protected
 */
gc.gis.ui.GCUserInterface.prototype._initUI = function()
{
	var self = this;

	// Hauptregionen
	this.frame = jQuery('<div>',{
		'class': 'gc-gis-frame-content'
	});
	jQuery('#'+ this.id ).append( jQuery('<div>', {
		'class': 'gc-gis-frame',
		html: this.frame
	}) );

	this.main = jQuery('<div>',{
		'class': 'gc-gis-main'
	});

	this.sidebar = new gc.gis.ui.GCSidebar({});
	jQuery('#'+ this.id ).append( this.sidebar.getElement() );
	this.sidebar.setContent( null );

	// Karten-Container
	var element_map = jQuery('#gc_map_view');
	if( !element_map.length )
	{
		element_map = jQuery('<div>', {
			id: 'gc_map_view',
			'class': 'gc-map-view gc-empty'
		});
	}
	this.attach( element_map, {
		target: this.main
	});

	// Header/Footer
	var element_header = jQuery('<div>', {
		id: 'gc_header',
		'class': 'gc-gis-header'
	});

	var element_footer = jQuery('<div>', {
		id: 'gc_footer',
		'class': 'gc-gis-footer'
	});

	this.attach( element_header, {
		target: this.frame
	});
	this.frame.append( jQuery('<div>', {
		'class': 'gc-gis-main-wrapper',
		html: this.main
	}) );

	this.attach( element_footer, {
		target: this.frame
	});
	element_header.hide();

	this.header = new gc.gis.ui.GCNavbar({
		target: '#gc_header',
		main: jQuery('<div>', {
			id: 'gc_header_main',
			'class': 'gc-header-left'
		}),
		auxiliary: jQuery('<div>', {
			id: 'gc_header_auxiliary',
			'class': 'gc-header-left'
		})
	});
	this.footer = new gc.gis.ui.GCNavbar({
		target: '#gc_footer',
		main: jQuery('<div>', {
			id: 'gc_footer_main',
			'class': 'gc-footer-left'
		}),
		auxiliary: jQuery('<div>', {
			id: 'gc_footer_auxiliary',
			'class': 'gc-footer-right'
		})
	});

	this.mapToolbarZoom = new gc.gis.ui.GCToolbar({
		className: 'gc-map-control-toolbar toolbar-vertical toolbar-zoom'
	});
	this.attach(this.mapToolbarZoom.getElement(), {
		target: this.main
	});

	this.mapToolbarRotate = new gc.gis.ui.GCToolbar({
		className: 'gc-map-control-toolbar toolbar-rotate'
	});
	this.attach(this.mapToolbarRotate.getElement(), {
		target: this.main
	});

	/**
	 * Dialog-Container
	 * @protected
	 * @type {jQuery}
     */
	this._dialog_container = jQuery('<div>', {
		'class': 'gc-dialog-container'
	});
	this._dialog_container.hide();
	gc.gis.iface.getElement().append( this._dialog_container );

	// Benachrichtigungen
	this._notification_area = jQuery('<ul>', {
		'class': 'gc-notification-area'
	});
	var notification_area_wrapper = jQuery('<div>', {
		'class': 'gc-notification-container',
		html: this._notification_area
	});
	gc.gis.iface.getElement().append( notification_area_wrapper );

	// Interaktionshilfe
	this._interaction_message_title = jQuery('<div>', {
		'class': 'gc-interaction-message-title'
	});
	this._interaction_message_content = jQuery('<div>', {
		'class': 'gc-interaction-message-content'
	});
	this._interaction_message_close_btn = jQuery('<button>', {
		'class': 'gc-interaction-message-close btn btn-primary',
		html: '<span class="gc-icon glyphicon glyphicon-remove"></span><span class="hidden">Schließen</span>',
		title: 'Schließen',
		click: function( evt ) {
			evt.preventDefault();
			try
			{
                self.unsetInteractionMessage( self._last_interaction_message );
			}
			catch( err )
			{
				gc.gis.iface.log.error( self, err );
			}
			jQuery(this).blur();
		}
	});
	this._interaction_message_exclude_btn = jQuery('<button>', {
		'class': 'gc-interaction-message-button btn btn-primary',
		html: '<span>Meldung nicht mehr anzeigen</span>',
		click: function( evt ) {
			evt.preventDefault();
			try
			{
				self.excludeInteractionMessage( self._last_interaction_message );
				self.unsetInteractionMessage( self._last_interaction_message );
			}
			catch( err )
			{
				gc.gis.iface.log.error( self, err );
			}
			jQuery(this).blur();
		}
	});
	this._interaction_message = jQuery('<div>', {
		'class': 'gc-interaction-message'
	});
	this._interaction_message
		.append( this._interaction_message_title )
		.append( this._interaction_message_content )
		.append( this._interaction_message_exclude_btn )
        .append( this._interaction_message_close_btn );

	this._interaction_message_container = jQuery('<div>', {
		'class': 'gc-interaction-message-container',
		html: this._interaction_message
	});
	this._interaction_message_container.hide();
	gc.gis.iface.getElement().append( this._interaction_message_container );

	// Lade-Indikator
	this._loading_indicator_label = jQuery('<p>', {
		'class': 'gc-loading-indicator-label',
		html: '<span class="gc-empty">Keine aktiven Prozesse.</span>'
	});
	this._loading_indicator = jQuery('<div>', {
		'class': 'gc-loading-indicator',
		html: this._loading_indicator_label
	});
	this._loading_indicator.hide();
	gc.gis.iface.getElement().append(this._loading_indicator);

	// Hängenbleiben von Boostrap-Tooltips verhindern, indem die Tooltips automatisch geschlossen werden
	jQuery(document).on('show.bs.tooltip', function (e) {
		setTimeout(function() {
			jQuery(e.target).tooltip('hide');
		}, 3000);
	});
};

/**
 * @protected
 */
gc.gis.ui.GCUserInterface.prototype._initEvents = function()
{
	var self = this;

	// Request listener
	gc.gis.iface.request.on( gc.gis.util.GCRequestManagerEvent.REQUEST_STARTED, this, function( evt ) {
		self.__onLoadingStarted();
	});
	gc.gis.iface.request.on( gc.gis.util.GCRequestManagerEvent.REQUEST_STOPPED, this, function( evt ) {
		self.__onLoadingStopped();
	});

	// Fullscreen listener
	document.addEventListener('fullscreenchange', this.__onFullscreenChange, false );
	document.addEventListener('mozfullscreenchange', this.__onFullscreenChange, false );
	document.addEventListener('webkitfullscreenchange', this.__onFullscreenChange, false );
	document.addEventListener('MSFullscreenChange', this.__onFullscreenChange, false );
};

/**
 * @public
 * @param {string} size: <code>xs</code>,<code>sm</code>,<code>md</code>,<code>lg</code>
 * @return {boolean}
 */
gc.gis.ui.GCUserInterface.prototype.matchMedia = function( size )
{
	if( typeof window.matchMedia === 'function' )
	{
        switch (size) {
            case 'xs':
				return window.matchMedia('(max-width: 26em)').matches;
            case 'sm':
				return window.matchMedia('(max-width: 36em)').matches;	// FIXME: to be determined
            case 'md':
                return window.matchMedia('(max-width: 46em)').matches;	// FIXME: to be determined
            case 'lg':
				return window.matchMedia('(max-width: 56em)').matches;	// FIXME: to be determined
        }
    }

	return false;
};

/**
 * @public
 * @param {jQuery|Vue} element Anzudockendes Element
 * @param {Object} options
 * @param {string|jQuery|gc.gis.widget.GCWidget} options.target Zielkontainer
 * @param {number} [options.order=] Index, an dem das Element eingefügt werdensoll
 */
gc.gis.ui.GCUserInterface.prototype.attach = function( element, options )
{
	gc.gis.assert.isSet(element);
	gc.gis.assert.isSet(options);

	if (options.target)
	{
		const order = options.order;
		let target = options.target;

		if (typeof target === 'string')
		{
			target = jQuery(target);
		}
		else if(target.getElement)
		{
			target = target.getElement();
			if (!(target instanceof jQuery))
			{
				target = jQuery(target);
			}
		}

		if (element instanceof Vue)
		{
			element.$mount();
			element = element.$el;
		}

		const children = target.children();
		if (order <= 0)
		{
			target.prepend(element);
		}
		else if (order > 0 && order < children.length)
		{
			children.eq(order).before(element);
		}
		else
		{
			target.append(element);
		}
	}
	else
	{
		gc.gis.iface.log.error( this, new gc.gis.error.GCError('Failed to attach element. No target.') );
	}
};

/**
 *
 */
gc.gis.ui.GCUserInterface.prototype.detach = function( widget, options )
{
	// TODO
};

/**
 * @public
 * @returns {boolean}
 */
gc.gis.ui.GCUserInterface.prototype.isNotificationActive = function()
{
	return this._notification_active;
};

/**
 * @public
 * @param {boolean} state
 */
gc.gis.ui.GCUserInterface.prototype.setNotificationsActive = function(state )
{
	this._notification_active = state;
};

/**
 * @public
 * @param {Object} options Aufrufoptionen
 * @param {string} [options.id]
 * @param {string} options.title
 * @param {string} options.type
 * @param {string} options.message
 */
gc.gis.ui.GCUserInterface.prototype.notify = function( options )
{
	if( this.isNotificationActive() !== true )
	{
		return;
	}

	var title = options.title;
	if( !title )
	{
		switch( options.type )
		{
			case gc.gis.ui.GCNotificationType.INFO:
				title = 'Info';
				break;
			case gc.gis.ui.GCNotificationType.WARNING:
				title = 'Warnung';
				break;
			case gc.gis.ui.GCNotificationType.ERROR:
				title = 'Fehler';
				break;
			case gc.gis.ui.GCNotificationType.DEBUG:
				title = 'Debug';
				break;
			default:
		}
	}
	var message = options.message;
	if( message instanceof Error && message.message )
	{
		message = message.message;
	}

	var element_title = jQuery('<h4>', {
		'class': 'gc-notification-title',
		html: title
	});
	var element_message = jQuery('<p>', {
		'class': 'gc-notification-message',
		html: message
	});
	var element_notification = jQuery('<li>', {
		'class': 'gc-notification gc-notification-type-'+ options.type
	});
	element_notification.append( element_title ).append(' ').append( element_message );
	element_notification.hide();
	this._notification_area.append( element_notification );
	element_notification.show('fade');

	setTimeout( function() {
		element_notification.hide('fade', {
			complete: function() {
				element_notification.remove();
			}
		});
	}, this._notification_timeout );
};

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.showDialog = function( options )
{
	var dlg = new gc.gis.widget.GCDialog( options );
	dlg.show();
};

/**
 * @public
 * @param {jQuery|Element} element
 * @param {Object<string,*>} [opt_options] Bootstrap Tooltip-Eigenschaften
 */
gc.gis.ui.GCUserInterface.prototype.tooltip = function(element, opt_options)
{
	if (!(element instanceof jQuery))
	{
		element = jQuery(element);
	}

	const options = opt_options || {};
	options.placement = options.placement || 'auto bottom';
	options.viewport = gc.gis.iface.getElement();
	options.container = gc.gis.iface.getElement();
	options.delay =  {show: 500, hide: 100};        // sollte Standard sein und nicht überschreibbar (?)

	element.tooltip(options);
};

/**
 * @param {string} selector
 * @param {Object} galleryOptions
 */
gc.gis.ui.GCUserInterface.prototype.setupLightGallery = function(selector, galleryOptions = {})
{
	const galleryMainElement = jQuery(selector);

	if (galleryMainElement.length)
	{
		import('lightgallery').then(function() {
			import('lg-autoplay').then(function () {});
			import('lg-fullscreen').then(function () {});
			import('lg-zoom').then(function () {});

			jQuery(function () {
				galleryMainElement.lightGallery(galleryOptions);
			});
		});
	}
};

/**
 * @private
 * @param {Object<string,string>} options
 */
gc.gis.ui.GCUserInterface.prototype.__getMessageIdentifier = function( options )
{
	return gc.gis.GCUtil.checksum( options.title + options.message );
};

/**
 * Prüft, ob die Benachrichtigung nicht mehr angezeigt werden soll.
 *
 * @public
 * @param {Object<string,string>} options Benachrichtigungsobjekt
 * @returns {boolean}
 */
gc.gis.ui.GCUserInterface.prototype.isInteractionMessageExcluded = function(options)
{
	var checksum = this.__getMessageIdentifier( options );
	return this._interactive_message_excludes[checksum] === true;
};

/**
 * @public
 * @param {Object<string,string>} options Benachrichtigungsobjekt
 */
gc.gis.ui.GCUserInterface.prototype.excludeInteractionMessage = function(options)
{
	var checksum = this.__getMessageIdentifier(options);

	if (!this._interactive_message_excludes[checksum])
	{
		this._interactive_message_excludes[checksum] = true;
		gc.gis.GCStorage.setItem(this.STORAGE_KEY, JSON.stringify(this._interactive_message_excludes),
			gc.gis.GCStorageType.LOCAL_STORAGE);

		var numNew = this.numMessageExludes();
		gc.gis.iface.ui._fireEvent(gc.gis.ui.GCUserInterfaceEvent.EXCLUDE_MESSAGES_CHANGED, {
			numOld: numNew - 1,
			numNew: numNew
		});
	}
};

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.setInteractionMessage = function(options)
{
	if(this.isInteractionMessageExcluded(options))
	{
		this.unsetInteractionMessage(options);
	}
	else if(options && (options.message || options.title))
	{
		this._interaction_message_queue.push(options);
		this._setInteractionMessage(options);
	}
	else
	{
		this.unsetInteractionMessage(options);
	}
};

/**
 * @private
 */
gc.gis.ui.GCUserInterface.prototype._setInteractionMessage = function( options )
{
	if( options && (options.message || options.title) )
	{
		// Ggf Icon setzen
		if( options.icon )
		{
			this._interaction_message.addClass( options.icon );
		}
		else
		{
			this._interaction_message.attr('class', 'gc-interaction-message');
		}

		// Ggf. Nachricht setzen
		if( options.message )
		{
			this._interaction_message_content.html('<p>'+ options.message +'</p>');
			this._interaction_message_content.show();
		}
		else
		{
			this._interaction_message_content.html('');
			this._interaction_message_content.hide();
		}

		// Ggf. Titel setzen
		if( options.title )
		{
			this._interaction_message_title.html('<strong>'+ options.title +'</strong>');
			this._interaction_message_title.show();
		}
		else
		{
			this._interaction_message_title.html('');
			this._interaction_message_title.hide();
		}

		// Nachricht anzeigen
		this._interaction_message_container.show('fade');

		this._last_interaction_message = options;
	}
};

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.unsetInteractionMessage = function( options )
{
	var self = this;

	for(var i=0; i<this._interaction_message_queue.length; i++ )
	{
		if( this._interaction_message_queue[i] === options )
		{
			this._interaction_message_queue.splice( i, 1 );
			break;
		}
	}

	if( this._interaction_message_queue.length > 0 )
	{
		this._setInteractionMessage( this._interaction_message_queue[this._interaction_message_queue.length-1] );
	}
	else
	{
		this._interaction_message.attr('class', 'gc-interaction-message');
		this._interaction_message_container.hide('fade', {
			complete: function() {
				var do_reset = true;
				if( self._last_interaction_message )
				{
					if( self._last_interaction_message.title !== options.title )
					{
						do_reset = false;
					}
					if( self._last_interaction_message.message !== options.message )
					{
						do_reset = false;
					}
				}
				if( do_reset === true )
				{
					self._interaction_message_title.html('');
					self._interaction_message_content.html('');
				}
			}
		});
	}
};

gc.gis.ui.GCUserInterface.prototype.toggleMapControls = function()
{
	var self = this;
	var is_visible = this.isMapControlsVisible();
    jQuery('.gc-gis-main .gc-map-control').not('.gc-map-control-drawingarea-clear').toggle('fade', {
		complete: function( evt ) {
			self._fireEvent( gc.gis.ui.GCUserInterfaceEvent.TOGGLE_MAP_CONTROLS, {
				active: !is_visible
			});
		}
	});
};

gc.gis.ui.GCUserInterface.prototype.isMapControlsVisible = function()
{
	return jQuery('.gc-gis-main .gc-map-control').not('.gc-map-control-drawingarea-clear').is(':visible');
};

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.toggleFooter = function()
{
	var self = this;

	var is_visible = this.isFooterVisible();

	var effect = {
        direction: 'down',
        complete: function( evt ) {
			if( is_visible )
			{
				self.footer.getElement().addClass('gc-collapsed');
			}
			if( !is_visible )
			{
				self.footer.getElement().removeClass('gc-collapsed');
			}
            self._fireEvent( gc.gis.ui.GCUserInterfaceEvent.TOGGLE_FOOTER, {
                active: !is_visible
            });
        }
    };
	if( is_visible )
	{
		self.footer.getElement().addClass('gc-collapsed');
		self.footer.getElement().children().children().hide('blind', effect );
	}
	else
	{
		self.footer.getElement().removeClass('gc-collapsed');
		self.footer.getElement().children().children().show('blind', effect );
	}
};

/**
 * @public
 * @returns {boolean}
 */
gc.gis.ui.GCUserInterface.prototype.isFooterVisible = function()
{
	return this.footer.getElement().children().children().is(':visible');
};

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.toggleFullscreen = function()
{
	if( this.isFullscreenEnabled() )
	{
		this.leaveFullscreen();
	}
	else
	{
		this.enterFullscreen();
	}
};

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.enterFullscreen = function()
{
	var element = gc.gis.iface.getElement()[0];
	var request = element.requestFullScreen
			   || element.webkitRequestFullscreen
			   || element.mozRequestFullScreen
			   || element.msRequestFullscreen;

	if( request )
	{
		request.call( element );
	}
	else
	{
		throw new gc.gis.error.GCUnsupportedActionError('Die Vollbildansicht wird von Ihrem Browser nicht unterstützt.');
	}
};

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.leaveFullscreen = function()
{
	if( document.exitFullscreen )
	{
		document.exitFullscreen();
	}
	else if( document.webkitExitFullscreen )
	{
		document.webkitExitFullscreen();
	}
	else if( document.mozCancelFullScreen )
	{
		document.mozCancelFullScreen();
	}
	else if( document.msExitFullscreen )
	{
		document.msExitFullscreen();
	}
	else
	{
		throw new gc.gis.error.GCUnsupportedActionError('Die Vollbildansicht wird von Ihrem Browser nicht unterstützt.');
	}
};

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.isFullscreenEnabled = function()
{
    return (document.fullscreenElement ? true : false)
        || (document.mozFullScreenElement ? true : false)
        || (document.webkitFullscreenElement ? true : false)
        || (document.msFullscreenElement ? true : false);
};

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.resetMessageExludes = function()
{
	var numOld = this.numMessageExludes();

	this._interactive_message_excludes = {};
	gc.gis.GCStorage.removeItem(this.STORAGE_KEY, gc.gis.GCStorageType.LOCAL_STORAGE);

	gc.gis.iface.ui._fireEvent(gc.gis.ui.GCUserInterfaceEvent.EXCLUDE_MESSAGES_CHANGED, {
		numOld: numOld,
		numNew: 0
	});
};

/**
 * @public
 */
gc.gis.ui.GCUserInterface.prototype.numMessageExludes = function()
{
	var num = 0;
	for (var prop in this._interactive_message_excludes)
	{
		num++;
	}

	return num;
};

/**
 * @private
 * @param evt
 */
gc.gis.ui.GCUserInterface.prototype.__onFullscreenChange = function( evt )
{
	gc.gis.iface.ui._fireEvent( gc.gis.ui.GCUserInterfaceEvent.FULLSCREEN_CHANGED, {
		active: gc.gis.iface.ui.isFullscreenEnabled()
	});
};

/**
 * @private
 * @param evt
 */
gc.gis.ui.GCUserInterface.prototype.__onLoadingStarted = function( evt )
{
	this.__updateLoadingCount();
	if( !this._loading_indicator.is(':visible') )
	{
		this._loading_indicator.show('fade');
	}
};

/**
 * @private
 * @param evt
 */
gc.gis.ui.GCUserInterface.prototype.__onLoadingStopped = function( evt )
{
	var count = this.__updateLoadingCount();
	if( count === 0 && this._loading_indicator.is(':visible') )
	{
		this._loading_indicator.hide('fade');
	}
};

gc.gis.ui.GCUserInterface.prototype.__updateLoadingCount = function()
{
	var processes = gc.gis.iface.request.getActiveProcesses();
	var count = processes.length;
	if( count === 0 )
	{
		this._loading_indicator_label.html(
			'<span class="gc-empty">Keine aktiven Prozesse.</span>'
		);
	}
    else if( count === 1 && processes[0].label )
	{
		this._loading_indicator_label.html( processes[0].label );
	}
	else
	{
		this._loading_indicator_label.html('Aktive Prozesse: '+ count );
	}
	return count;
};
